import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Stethoscope,
  Edit,
  MoreHorizontal,
  Trash2,
  Clock,
  Info,
  ShieldCheck,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const categoryColors = {
  iv_therapy: "bg-blue-100 text-blue-700",
  blood_filtration: "bg-red-100 text-red-700",
  detox_therapy: "bg-green-100 text-green-700",
  diagnostic: "bg-purple-100 text-purple-700",
  consultation: "bg-indigo-100 text-indigo-700",
  wellness: "bg-teal-100 text-teal-700",
  procedure: "bg-orange-100 text-orange-700",
};

const statusColors = {
  active: "bg-green-100 text-green-700",
  inactive: "bg-slate-100 text-slate-500",
  draft: "bg-yellow-100 text-yellow-700",
};

const formatStatus = (status) => {
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Active";
};

const formatCategoryName = (category) => {
  return category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export default function ServiceList({
  services,
  isLoading,
  onEditService,
  onDeleteService,
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-8 w-1/4 mb-4" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full mt-2" />
          <Skeleton className="h-12 w-full mt-2" />
        </CardContent>
      </Card>
    );
  }

  if (services.length === 0) {
    return (
      <Card className="text-center p-12">
        <Stethoscope className="mx-auto h-12 w-12 text-slate-400" />
        <h3 className="mt-2 text-xl font-semibold">No services found</h3>
        <p className="mt-1 text-sm text-slate-500">
          Get started by creating a new service.
        </p>
      </Card>
    );
  }

  console.log('[DEBUG] ServiceList rendering with services:', services.map((s, idx) => ({
    index: idx,
    id: s.id,
    name: s.service_name
  })));

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/60">
                <TableHead>Service</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Requirements</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service, index) => {
                // Capture the service ID in the closure to avoid any reference issues
                const serviceId = service.id;
                const serviceName = service.service_name;

                return (
                <TableRow key={service.id} className="hover:bg-slate-50/60" data-testid={`service-row-${index}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900">
                      {service.service_name}
                    </div>
                    <div className="text-sm text-slate-500 line-clamp-2">
                      {service.description}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={categoryColors[service.category]}>
                      {formatCategoryName(service.category)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <span>{service.duration_minutes} min</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-2">
                      {service.requires_physician && (
                        <Badge variant="outline" className="text-xs w-fit">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Physician
                        </Badge>
                      )}
                      {service.requires_lab_work && (
                        <Badge variant="outline" className="text-xs w-fit">
                          <Info className="w-3 h-3 mr-1" />
                          Lab Work
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[service.status] || statusColors.active}>
                      {formatStatus(service.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`service-actions-${index}`}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          data-testid={`edit-service-${index}`}
                          onClick={(e) => {
                            e.preventDefault();
                            onEditService(service);
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          <span>Edit</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          data-testid={`delete-service-${index}`}
                          onClick={(e) => {
                            e.preventDefault();
                            onDeleteService(serviceId);
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )})}

            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
